import React, { useState, useEffect } from 'react';
import { arztService } from '../../services/arzt.service';
import { Arzt, Praxis, AppType } from '../../types';
import { useEnvironment } from '../../context/EnvironmentContext';

interface ArztFormProps {
  arzt?: Arzt | null;
  availablePraxen: Praxis[];
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormData {
  vorname: string;
  nachname: string;
  qualifikation: string;
  email: string;
  telefon: string;
  // Map of praxisId -> array of selected days
  associations: Record<number, string[]>;
}

interface FormErrors {
  vorname?: string;
  nachname?: string;
  email?: string;
  associations?: string;
}

const DAYS_OF_WEEK = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

const ArztForm: React.FC<ArztFormProps> = ({ arzt, availablePraxen, onSuccess, onCancel }) => {
  const { currentApp } = useEnvironment();
  const [formData, setFormData] = useState<FormData>({
    vorname: '',
    nachname: '',
    qualifikation: '',
    email: '',
    telefon: '',
    associations: {},
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (arzt) {
      const initialAssociations: Record<number, string[]> = {};

      // Load current associations if available
      if (arzt.praxisAssociations) {
        arzt.praxisAssociations.forEach(assoc => {
          initialAssociations[assoc.praxisId] = assoc.arbeitstage || [];
        });
      } else if (arzt.praxen && arzt.arbeitstage) {
        // Fallback for legacy data/state: apply global workdays to all associated practices
        arzt.praxen.forEach(p => {
          initialAssociations[p.id] = arzt.arbeitstage || [];
        });
      }

      setFormData({
        vorname: arzt.vorname,
        nachname: arzt.nachname,
        qualifikation: arzt.qualifikation || '',
        email: arzt.email || '',
        telefon: arzt.telefon || '',
        associations: initialAssociations,
      });
    } else {
      // Default for new doctor: No associations initially
      setFormData({
        vorname: '',
        nachname: '',
        qualifikation: '',
        email: '',
        telefon: '',
        associations: {},
      });
    }
  }, [arzt]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.vorname.trim()) {
      newErrors.vorname = 'Vorname ist erforderlich';
    }

    if (!formData.nachname.trim()) {
      newErrors.nachname = 'Nachname ist erforderlich';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse';
    }

    const praxisIds = Object.keys(formData.associations);
    if (praxisIds.length === 0) {
      newErrors.associations = 'Bitte wählen Sie mindestens eine Praxis aus.';
    } else {
      // Check if at least one day is selected for each selected practice
      const emptyPractice = praxisIds.find(id => formData.associations[parseInt(id)].length === 0);
      if (emptyPractice) {
        const pName = availablePraxen.find(p => p.id === parseInt(emptyPractice))?.name || 'Praxis';
        newErrors.associations = `Bitte wählen Sie mindestens einen Arbeitstag für ${pName} aus.`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handlePraxisToggle = (praxisId: number, checked: boolean) => {
    setFormData(prev => {
      const newAssociations = { ...prev.associations };
      if (checked) {
        // Enable practice with all days selected by default for convenience
        newAssociations[praxisId] = [...DAYS_OF_WEEK];
      } else {
        // Remove practice
        delete newAssociations[praxisId];
      }
      return { ...prev, associations: newAssociations };
    });

    if (errors.associations) {
      setErrors(prev => ({ ...prev, associations: undefined }));
    }
  };

  const handleDayToggle = (praxisId: number, day: string, checked: boolean) => {
    setFormData(prev => {
      const currentDays = prev.associations[praxisId] || [];
      const newDays = checked
        ? [...currentDays, day]
        : currentDays.filter(d => d !== day);

      return {
        ...prev,
        associations: {
          ...prev.associations,
          [praxisId]: newDays
        }
      };
    });
  };

  const handleSelectAllDays = (praxisId: number) => {
    setFormData(prev => ({
      ...prev,
      associations: {
        ...prev.associations,
        [praxisId]: [...DAYS_OF_WEEK]
      }
    }));
  };

  const handleDeselectAllDays = (praxisId: number) => {
    setFormData(prev => ({
      ...prev,
      associations: {
        ...prev.associations,
        [praxisId]: []
      }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);
    setErrorMessage(null);

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // Convert associations map to API format
      const associationsList = Object.entries(formData.associations).map(([praxisIdStr, days]) => ({
        praxisId: parseInt(praxisIdStr),
        arbeitstage: days
      }));

      const arztData = {
        vorname: formData.vorname.trim(),
        nachname: formData.nachname.trim(),
        qualifikation: formData.qualifikation.trim() || undefined,
        email: formData.email.trim() || undefined,
        telefon: formData.telefon.trim() || undefined,
        associations: associationsList,
        apps: [currentApp as AppType],
      };

      if (arzt) {
        await arztService.updateArzt(arzt.id, arztData);
        setSuccessMessage('Arzt erfolgreich aktualisiert');
      } else {
        await arztService.createArzt(arztData);
        setSuccessMessage('Arzt erfolgreich erstellt');
      }

      setTimeout(() => {
        onSuccess();
      }, 1000);
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Fehler beim Speichern des Arztes');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">
        {arzt ? 'Arzt bearbeiten' : 'Neuer Arzt'}
      </h2>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {errorMessage}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="vorname" className="block text-sm font-medium text-gray-700 mb-1">
              Vorname <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="vorname"
              name="vorname"
              value={formData.vorname}
              onChange={handleChange}
              className={`w-full px-3 py-3 min-h-[44px] text-base border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.vorname ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {errors.vorname && (
              <p className="text-red-500 text-sm mt-1">{errors.vorname}</p>
            )}
          </div>

          <div>
            <label htmlFor="nachname" className="block text-sm font-medium text-gray-700 mb-1">
              Nachname <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="nachname"
              name="nachname"
              value={formData.nachname}
              onChange={handleChange}
              className={`w-full px-3 py-3 min-h-[44px] text-base border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.nachname ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {errors.nachname && (
              <p className="text-red-500 text-sm mt-1">{errors.nachname}</p>
            )}
          </div>
        </div>

        <div className="mb-4">
          <label htmlFor="qualifikation" className="block text-sm font-medium text-gray-700 mb-1">
            Qualifikation
          </label>
          <input
            type="text"
            id="qualifikation"
            name="qualifikation"
            value={formData.qualifikation}
            onChange={handleChange}
            className="w-full px-3 py-3 min-h-[44px] text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="z.B. Facharzt für Allgemeinmedizin"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              E-Mail
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-3 py-3 min-h-[44px] text-base border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <label htmlFor="telefon" className="block text-sm font-medium text-gray-700 mb-1">
              Telefon
            </label>
            <input
              type="tel"
              id="telefon"
              name="telefon"
              value={formData.telefon}
              onChange={handleChange}
              className="w-full px-3 py-3 min-h-[44px] text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-lg font-medium text-gray-800 mb-3 border-b pb-2">
            Verfügbarkeit in Praxen
          </label>

          {errors.associations && (
            <div className="mb-4 text-red-500 text-sm bg-red-50 p-2 rounded">
              {errors.associations}
            </div>
          )}

          <div className="space-y-6">
            {availablePraxen.length === 0 && (
              <p className="text-sm text-gray-500 italic">Keine Praxen verfügbar.</p>
            )}

            {availablePraxen.map((praxis) => {
              const isSelected = !!formData.associations[praxis.id];
              const selectedDays = formData.associations[praxis.id] || [];

              return (
                <div key={praxis.id} className={`border rounded-md p-4 transition-colors ${isSelected ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex items-center mb-3">
                    <input
                      type="checkbox"
                      id={`praxis-${praxis.id}`}
                      checked={isSelected}
                      onChange={(e) => handlePraxisToggle(praxis.id, e.target.checked)}
                      className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                    />
                    <label htmlFor={`praxis-${praxis.id}`} className="block text-base font-semibold text-gray-800 cursor-pointer select-none">
                      {praxis.name} {praxis.stadt ? `- ${praxis.stadt}` : ''}
                    </label>
                  </div>

                  {isSelected && (
                    <div className="ml-8 mt-2 animate-fadeIn">
                      <div className="flex justify-between items-center mb-2">
                        <p className="text-sm font-medium text-gray-700">Arbeitstage:</p>
                        <div className="space-x-2">
                          <button
                            type="button"
                            onClick={() => handleSelectAllDays(praxis.id)}
                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                          >
                            Alle
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            type="button"
                            onClick={() => handleDeselectAllDays(praxis.id)}
                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                          >
                            Keine
                          </button>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {DAYS_OF_WEEK.map((day) => (
                          <label key={`${praxis.id}-${day}`} className={`
                            inline-flex items-center px-3 py-2 rounded-full border text-sm cursor-pointer transition-colors select-none
                            ${selectedDays.includes(day)
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'}
                          `}>
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={selectedDays.includes(day)}
                              onChange={(e) => handleDayToggle(praxis.id, day, e.target.checked)}
                            />
                            <span>{day.substring(0, 2)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 mt-8 pt-4 border-t">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-3 min-h-[44px] border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-3 min-h-[44px] bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {loading ? 'Speichert...' : arzt ? 'Aktualisieren' : 'Erstellen'}
          </button>
        </div>
      </form >
    </div >
  );
};

export default ArztForm;
